import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {ArrowLeft, Save} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Product {
  _id: string
  name: string
  nameEn?: string
  slug: string
  category: string
  price: number
  originalPrice?: number
  costPrice?: number
  description: string
  descriptionEn?: string
  images: string[]
  origin?: string
  originEn?: string
  region?: string
  regionEn?: string
  brewingMethods?: string[]
  brewingMethodsEn?: string[]
  flavorNotes?: string[]
  flavorNotesEn?: string[]
  specifications?: {
    roast?: string
    roastEn?: string
    variety?: string
    varietyEn?: string
    altitude?: string
    processing?: string
    processingEn?: string
    tastingNotes?: string
    tastingNotesEn?: string
    weight?: string
  }
  inventory: number
  status: 'active' | 'inactive' | 'draft'
  isFeatured: boolean
  seoTitle?: string
  seoDescription?: string
  createdAt: string
  updatedAt: string
}

const ProductEditor: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  const [loading, setLoading] = useState(isEditing)
  const [product, setProduct] = useState<Product | null>(null)
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([])

  useEffect(() => {
    fetchCategories()
    if (isEditing && id) {
      fetchProduct(id)
    }
  }, [isEditing, id])

  const fetchCategories = async () => {
    try {
      const response = await lumi.entities.product_categories.list({
        filter: { status: 'active' },
        sort: { sortOrder: 1 }
      })
      const categoryList = (response.list || []).map((cat: any) => ({
        value: cat.value,
        label: cat.label
      }))
      setCategories(categoryList)
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchProduct = async (productId: string) => {
    try {
      const data = await lumi.entities.products.get(productId)
      setProduct(data)
    } catch (error) {
      console.error('Failed to fetch product:', error)
      toast.error('載入商品失敗')
      navigate('/admin/products')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    
    const productData = {
      name: formData.get('name') as string,
      nameEn: formData.get('nameEn') as string,
      slug: formData.get('slug') as string,
      category: formData.get('category') as string,
      price: Number(formData.get('price')),
      originalPrice: Number(formData.get('originalPrice')) || 0,
      costPrice: Number(formData.get('costPrice')) || 0,
      description: formData.get('description') as string,
      descriptionEn: formData.get('descriptionEn') as string,
      images: (formData.get('images') as string).split('\n').filter(img => img.trim()),
      origin: formData.get('origin') as string,
      originEn: formData.get('originEn') as string,
      region: formData.get('region') as string,
      regionEn: formData.get('regionEn') as string,
      brewingMethods: (formData.get('brewingMethods') as string).split(/[,，、]/).map(s => s.trim()).filter(Boolean),
      brewingMethodsEn: (formData.get('brewingMethodsEn') as string).split(/[,，、]/).map(s => s.trim()).filter(Boolean),
      flavorNotes: (formData.get('flavorNotes') as string).split(/[,，、]/).map(s => s.trim()).filter(Boolean),
      flavorNotesEn: (formData.get('flavorNotesEn') as string).split(/[,，、]/).map(s => s.trim()).filter(Boolean),
      specifications: {
        roast: formData.get('roast') as string,
        roastEn: formData.get('roastEn') as string,
        variety: formData.get('variety') as string,
        varietyEn: formData.get('varietyEn') as string,
        altitude: formData.get('altitude') as string,
        processing: formData.get('processing') as string,
        processingEn: formData.get('processingEn') as string,
        tastingNotes: formData.get('tastingNotes') as string,
        tastingNotesEn: formData.get('tastingNotesEn') as string,
        weight: formData.get('weight') as string
      },
      inventory: Number(formData.get('inventory')),
      status: formData.get('status') as 'active' | 'inactive' | 'draft',
      isFeatured: formData.get('isFeatured') === 'true',
      seoTitle: formData.get('seoTitle') as string,
      seoDescription: formData.get('seoDescription') as string,
      creator: 'admin',
      createdAt: product?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    try {
      if (isEditing && id) {
        await lumi.entities.products.update(id, productData)
        toast.success('商品更新成功')
      } else {
        await lumi.entities.products.create(productData)
        toast.success('商品創建成功')
      }
      navigate('/admin/products')
    } catch (error) {
      console.error('Product operation failed:', error)
      toast.error(isEditing ? '商品更新失敗' : '商品創建失敗')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-900 text-gray-100">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/admin/products')}
            className="mr-4 p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <ArrowLeft size={24} className="text-gray-400" />
          </button>
          <div>
            <h1 className="text-3xl font-light tracking-wider mb-2">
              {isEditing ? '編輯商品' : '新增商品'}
            </h1>
            <p className="text-gray-400">
              {isEditing ? '修改商品詳細資訊' : '建立新的商品項目'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-gray-950 border border-gray-800 p-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">商品名稱 *</label>
              <input
                name="name"
                type="text"
                defaultValue={product?.name || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">英文名稱</label>
              <input
                name="nameEn"
                type="text"
                defaultValue={product?.nameEn || ''}
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">URL標識符 (Slug) *</label>
              <input
                name="slug"
                type="text"
                defaultValue={product?.slug || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">分類 *</label>
              <select
                name="category"
                defaultValue={product?.category || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              >
                <option value="">選擇分類</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">售價 *</label>
              <input
                name="price"
                type="number"
                step="1"
                defaultValue={product?.price || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">原價 (顯示刪除線)</label>
              <input
                name="originalPrice"
                type="number"
                step="1"
                defaultValue={product?.originalPrice || ''}
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">庫存 *</label>
              <input
                name="inventory"
                type="number"
                defaultValue={product?.inventory || ''}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Descriptions */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">商品描述 *</label>
            <textarea
              name="description"
              rows={4}
              defaultValue={product?.description || ''}
              required
              className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">英文描述</label>
            <textarea
              name="descriptionEn"
              rows={4}
              defaultValue={product?.descriptionEn || ''}
              className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">商品圖片 (每行一個URL)</label>
            <textarea
              name="images"
              rows={3}
              defaultValue={product?.images?.join('\n') || ''}
              placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
              className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
            />
          </div>

          {/* Detailed Info */}
          <div className="border border-gray-800 p-4">
            <h3 className="text-lg font-light text-gray-100 mb-4">詳細資訊</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">產地 (Origin)</label>
                <input
                  name="origin"
                  type="text"
                  defaultValue={product?.origin || ''}
                  placeholder="例：衣索比亞"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">產地 (Origin) - 英文</label>
                <input
                  name="originEn"
                  type="text"
                  defaultValue={product?.originEn || ''}
                  placeholder="Ex: Ethiopia"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">區域 (Region)</label>
                <input
                  name="region"
                  type="text"
                  defaultValue={product?.region || ''}
                  placeholder="例：耶加雪菲"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">區域 (Region) - 英文</label>
                <input
                  name="regionEn"
                  type="text"
                  defaultValue={product?.regionEn || ''}
                  placeholder="Ex: Yirgacheffe"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">風味關鍵字 (用逗號分隔)</label>
                <input
                  name="flavorNotes"
                  type="text"
                  defaultValue={product?.flavorNotes?.join('、') || ''}
                  placeholder="例：花香、柑橘、蜂蜜"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">風味關鍵字 (Flavor Notes) - 英文</label>
                <input
                  name="flavorNotesEn"
                  type="text"
                  defaultValue={product?.flavorNotesEn?.join(', ') || ''}
                  placeholder="Ex: Floral, Citrus, Honey"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">建議沖煮方式 (用逗號分隔)</label>
                <input
                  name="brewingMethods"
                  type="text"
                  defaultValue={product?.brewingMethods?.join('、') || ''}
                  placeholder="例：手沖、V60、愛樂壓"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">建議沖煮方式 (Brewing Methods) - 英文</label>
                <input
                  name="brewingMethodsEn"
                  type="text"
                  defaultValue={product?.brewingMethodsEn?.join(', ') || ''}
                  placeholder="Ex: Pour Over, V60, AeroPress"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Bean Specifications */}
          <div className="border border-gray-800 p-4">
            <h3 className="text-lg font-light text-gray-100 mb-4">豆單規格 (咖啡豆專用)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">烘焙程度</label>
                <input
                  name="roast"
                  type="text"
                  defaultValue={product?.specifications?.roast || ''}
                  placeholder="例：淺中焙"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">烘焙程度 (Roast) - 英文</label>
                <input
                  name="roastEn"
                  type="text"
                  defaultValue={product?.specifications?.roastEn || ''}
                  placeholder="Ex: Light-Medium Roast"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">品種</label>
                <input
                  name="variety"
                  type="text"
                  defaultValue={product?.specifications?.variety || ''}
                  placeholder="例：衣索比亞原生種"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">品種 (Variety) - 英文</label>
                <input
                  name="varietyEn"
                  type="text"
                  defaultValue={product?.specifications?.varietyEn || ''}
                  placeholder="Ex: Heirloom"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">海拔</label>
                <input
                  name="altitude"
                  type="text"
                  defaultValue={product?.specifications?.altitude || ''}
                  placeholder="例：1900-2200m"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">重量規格</label>
                <input
                  name="weight"
                  type="text"
                  defaultValue={product?.specifications?.weight || ''}
                  placeholder="例：200g / 1kg"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">處理法</label>
                <input
                  name="processing"
                  type="text"
                  defaultValue={product?.specifications?.processing || ''}
                  placeholder="例：水洗處理"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">處理法 (Processing) - 英文</label>
                <input
                  name="processingEn"
                  type="text"
                  defaultValue={product?.specifications?.processingEn || ''}
                  placeholder="Ex: Washed"
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">風味描述 (完整句子)</label>
                <input
                  name="tastingNotes"
                  type="text"
                  defaultValue={product?.specifications?.tastingNotes || ''}
                  placeholder="例：入口帶有明亮的花香..."
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">風味描述 (Tasting Notes) - 英文</label>
                <input
                  name="tastingNotesEn"
                  type="text"
                  defaultValue={product?.specifications?.tastingNotesEn || ''}
                  placeholder="Ex: Bright floral notes with..."
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Internal Cost */}
          <div className="border border-gray-800 p-4">
            <h3 className="text-lg font-light text-gray-100 mb-4">內部成本</h3>
            <div>
              <label className="block text-sm text-gray-400 mb-2">成本價</label>
              <input
                name="costPrice"
                type="number"
                step="0.01"
                defaultValue={product?.costPrice || ''}
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              />
            </div>
          </div>

          {/* SEO */}
          <div className="border border-gray-800 p-4">
            <h3 className="text-lg font-light text-gray-100 mb-4">SEO設定</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">SEO標題</label>
                <input
                  name="seoTitle"
                  type="text"
                  defaultValue={product?.seoTitle || ''}
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">SEO描述</label>
                <textarea
                  name="seoDescription"
                  rows={3}
                  defaultValue={product?.seoDescription || ''}
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Status & Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">狀態 *</label>
              <select
                name="status"
                defaultValue={product?.status || 'draft'}
                required
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              >
                <option value="draft">草稿</option>
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">精選商品</label>
              <select
                name="isFeatured"
                defaultValue={product?.isFeatured?.toString() || 'false'}
                className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
              >
                <option value="false">否</option>
                <option value="true">是</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-800">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-6 py-3 border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-gray-100 transition-colors duration-200 text-sm font-light tracking-wider"
            >
              取消
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
            >
              <Save size={16} className="mr-2" />
              {isEditing ? '更新商品' : '創建商品'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProductEditor